import React, { useContext, useState } from 'react';
import { useQuery, useApolloClient } from '@apollo/react-hooks';
import { GET_RESTRICTED_DEVELOPER_CONTRIBUTIONS, GET_RESTRICTED_DEVELOPER_CONTRIBUTIONS_COUNT } from '../Queries';
import { UserContext } from '../App';
import { Container, Paper, Button, Typography, Box, Alert, LinearProgress } from '@mui/material';
import { Spinner } from 'reactstrap';
import DownloadIcon from '@mui/icons-material/Download';
import HomeIcon from '@mui/icons-material/Home';
import { Link } from 'react-router-dom';
import MUIDataTable from "mui-datatables";

const CEFSContributorsDevelopers = () => {
  const userInfo = useContext(UserContext);
  const client = useApolloClient();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [downloadAllError, setDownloadAllError] = useState(null);
  const [downloadProgress, setDownloadProgress] = useState(null); // { fetched, total }

  const { loading, error, data } = useQuery(GET_RESTRICTED_DEVELOPER_CONTRIBUTIONS, {
    variables: {
      limit: rowsPerPage + 1,
      offset: page * rowsPerPage,
    },
    fetchPolicy: "network-only"
  });

  // Check if user is admin
  const isAdmin = userInfo?.userSecLvl === "AD";

  if (!isAdmin) {
    return (
      <Container>
        <Alert severity="error">You do not have permission to access this page.</Alert>
        <br />
        <Button component={Link} to="/Home" className="yellowButton">
          <HomeIcon /> Return to Main Menu
        </Button>
      </Container>
    );
  }

  if (loading) return <Spinner color="dark" />;
  if (error) return <Alert severity="error">Error loading data: {error.message}</Alert>;

  const contributionsData = data?.getRestrictedDeveloperContributions || [];
  const hasNextPage = contributionsData.length > rowsPerPage;
  
  // Transform the data from { data: {...} } format to flat array
  const flatData = contributionsData.slice(0, rowsPerPage).map(item => item.data || {});

  // If no data, return empty state
  if (flatData.length === 0) {
    return (
      <Container>
        <Paper style={{ padding: '20px', marginTop: '20px' }}>
          <Typography variant="h4" gutterBottom>
            CEFS Contributors and Developers
          </Typography>
          <Alert severity="info">No data available for the current page.</Alert>
          <br />
          <Button component={Link} to="/Admin" className="yellowButton">
            <HomeIcon /> Return to Admin Menu
          </Button>
        </Paper>
      </Container>
    );
  }

  // Dynamically generate columns based on the first row's keys
  const sampleRow = flatData[0];
  const columns = Object.keys(sampleRow).map(key => ({
    name: key,
    label: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
    options: {
      filter: true,
      sort: true,
      display: true,
    }
  }));

  const downloadRowsAsCSV = (rows, fileSuffix) => {
    if (rows.length === 0) return;

    // Get all unique keys from all rows
    const allKeys = new Set();
    rows.forEach(row => {
      Object.keys(row).forEach(key => allKeys.add(key));
    });

    const headers = Array.from(allKeys);
    const csvRows = [headers.join(',')];

    rows.forEach(row => {
      const csvRow = headers.map(header => {
        const value = row[header];
        // Handle null/undefined and escape quotes
        if (value === null || value === undefined) return '""';
        const stringValue = String(value).replace(/"/g, '""');
        return `"${stringValue}"`;
      });
      csvRows.push(csvRow.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `cefs_contributors_developers${fileSuffix}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadCSV = () => downloadRowsAsCSV(flatData, '');

  const handleDownloadAllCSV = async () => {
    setDownloadingAll(true);
    setDownloadAllError(null);
    setDownloadProgress(null);

    try {
      // Get the total count first so we can show progress
      const countResult = await client.query({
        query: GET_RESTRICTED_DEVELOPER_CONTRIBUTIONS_COUNT,
        fetchPolicy: 'network-only',
      });
      const total = countResult.data?.getRestrictedDeveloperContributionsCount || 0;
      setDownloadProgress({ fetched: 0, total });

      // The server caps each request at 500 rows, so page through in batches
      const batchSize = 500;
      const allRows = [];
      let offset = 0;

      while (true) {
        const result = await client.query({
          query: GET_RESTRICTED_DEVELOPER_CONTRIBUTIONS,
          variables: { limit: batchSize, offset },
          fetchPolicy: 'network-only',
        });

        const batch = (result.data?.getRestrictedDeveloperContributions || [])
          .map(item => item.data || {});
        allRows.push(...batch);
        setDownloadProgress({ fetched: allRows.length, total });

        if (batch.length < batchSize) break;
        offset += batchSize;
      }

      downloadRowsAsCSV(allRows, '_all');
    } catch (err) {
      setDownloadAllError(err.message);
    } finally {
      setDownloadingAll(false);
      setDownloadProgress(null);
    }
  };

  const options = {
    filterType: 'dropdown',
    rowsPerPage: 25,
    rowsPerPageOptions: [10, 25, 50, 100],
    selectableRows: false,
    download: true,
    print: false,
    viewColumns: true,
    search: true,
  };

  return (
    <Container>
      <Paper style={{ padding: '20px', marginTop: '20px' }}>
        <Typography variant="h4" gutterBottom>
          CEFS Contributors and Developers
        </Typography>
        <Typography variant="body1" gutterBottom>
          View and export data from the restricted developer contributions view.
        </Typography>
        <Typography variant="body2" style={{ marginBottom: '20px', fontStyle: 'italic' }}>
          Showing page {page + 1} with up to {rowsPerPage} records per page.
        </Typography>
        
        <Box 
          style={{ 
            marginBottom: '30px',
            padding: '20px',
            backgroundColor: '#f5f5f5',
            borderRadius: '8px',
            border: '1px solid #e0e0e0'
          }}
        >
          <Typography variant="h6" style={{ marginBottom: '12px', fontWeight: 600 }}>
            Export Data
          </Typography>
          <Typography variant="body2" style={{ marginBottom: '16px', color: '#666' }}>
            Download the records currently loaded in the table, or all records, as a CSV file.
          </Typography>
          <Box style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', justifyContent: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadCSV}
              className="yellowButton"
              size="large"
              style={{
                minWidth: '200px',
                padding: '10px 24px',
                fontSize: '16px',
                fontWeight: 600,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              Download Current Page CSV
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={downloadingAll ? <Spinner size="sm" color="dark" /> : <DownloadIcon />}
              onClick={handleDownloadAllCSV}
              disabled={downloadingAll}
              className="yellowButton"
              size="large"
              style={{
                minWidth: '200px',
                padding: '10px 24px',
                fontSize: '16px',
                fontWeight: 600,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              {downloadingAll
                ? (downloadProgress && downloadProgress.total > 0
                    ? `Downloading ${downloadProgress.fetched.toLocaleString()} of ${downloadProgress.total.toLocaleString()}...`
                    : 'Downloading All...')
                : 'Download All CSV'}
            </Button>
          </Box>
          {downloadingAll && downloadProgress && downloadProgress.total > 0 && (
            <Box style={{ marginTop: '16px' }}>
              <LinearProgress
                variant="determinate"
                value={Math.min((downloadProgress.fetched / downloadProgress.total) * 100, 100)}
              />
              <Typography variant="body2" style={{ marginTop: '4px', color: '#666' }}>
                Fetched {downloadProgress.fetched.toLocaleString()} of {downloadProgress.total.toLocaleString()} records
              </Typography>
            </Box>
          )}
          {downloadAllError && (
            <Alert severity="error" style={{ marginTop: '16px' }}>
              Error downloading all records: {downloadAllError}
            </Alert>
          )}
        </Box>

        <MUIDataTable
          title="CEFS Contributors and Developers"
          data={flatData}
          columns={columns}
          options={{
            ...options,
            serverSide: true,
            count: hasNextPage
              ? ((page + 1) * rowsPerPage) + 1
              : (page * rowsPerPage) + flatData.length,
            page,
            rowsPerPage,
            onTableChange: (action, tableState) => {
              if (action === "changePage") {
                setPage(tableState.page);
              }

              if (action === "changeRowsPerPage") {
                setRowsPerPage(tableState.rowsPerPage);
                setPage(0);
              }
            }
          }}
        />

        <br />
        <Button
          component={Link}
          to="/Admin"
          className="yellowButton"
        >
          <HomeIcon /> Return to Admin Menu
        </Button>
      </Paper>
    </Container>
  );
};

export default CEFSContributorsDevelopers;

